/**
 * InstalmentsCalculator — Batch #14.329 / #14.330 : calculateur d'acomptes provisionnels réutilisable selon la portée.
 *   scope "cabinet" → MES acomptes de travailleur autonome (revenu brut − dépenses, province, âge, RRQ/RQAP/AE) — aucun dossier
 *   scope "fiscal"  → particulier d'un dossier fiscal : impôt payé l'an dernier + % d'augmentation → acomptes
 *   scope "books"   → dossier Tenue de livres : travailleur autonome (estimation complète) ou société
 * Résultats : acomptes requis ?, 3 méthodes, calendrier combiné, PDF, plan enregistré (alimente le calendrier de conformité du dossier).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { FileDown, Save, Trash2 } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, Field, downloadPostPdf, errMsg, inputCls, money } from "@/pages/admin/comptabilite/_shared";
import SelfEmployedForm from "./SelfEmployedForm";
import SelfEmployedDetail from "./SelfEmployedDetail";

const num = (v) => { const n = parseFloat(String(v ?? "").replace(/\s/g, "").replace(",", ".")); return Number.isFinite(n) ? n : 0; };
const numOrNull = (v) => (String(v ?? "").trim() === "" ? null : num(v));
const YEAR = new Date().getFullYear();
const J = { fed: "Fédéral (ARC)", qc: "Québec (Revenu Québec)" };
const KIND_LABEL = { individual: "particulier", self_employed: "travailleur autonome", corporation: "société" };

function PriorTaxInputs({ jk, v, set, labels, hint }) {
  return (
    <div className="bg-white border rounded-xl p-3 space-y-2" data-testid={`inst-inputs-${jk}`}>
      <h4 className="text-xs font-bold text-slate-700">{J[jk]}</h4>
      {hint && <p className="text-[11px] text-slate-500">{hint}</p>}
      {labels.map(([k, l]) => (
        <Field key={k} label={l}><input type="text" inputMode="decimal" className={`${inputCls} font-mono text-right`} value={v[k]} onChange={(e) => set(jk, k, e.target.value)} placeholder="0,00" data-testid={`inst-${jk}-${k}`} /></Field>
      ))}
    </div>
  );
}

function Methods({ jk, j, chosen, onChoose, estimatedFromPrior }) {
  if (!j) return null;
  return (
    <div className={`bg-white border rounded-xl p-3 ${j.required ? "border-slate-200" : "border-emerald-200"}`} data-testid={`inst-result-${jk}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h4 className="text-sm font-bold text-slate-800">{J[jk]}</h4>
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${j.required ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`} data-testid={`inst-required-${jk}`}>{j.required ? "Acomptes requis" : "Aucun acompte requis"}</span>
      </div>
      <p className="text-xs text-slate-600 mt-1">{j.why}{estimatedFromPrior && <span className="text-slate-400"> (estimation = impôt de l'an dernier × augmentation)</span>}</p>
      {j.required && (
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-xs" data-testid={`inst-methods-${jk}`}>
            <thead className="text-slate-500"><tr><th className="text-left px-2 py-1">Méthode</th>{j.methods[0].schedule.map((s) => <th key={s.due} className="text-right px-2 py-1 whitespace-nowrap">{s.due}</th>)}<th className="text-right px-2 py-1">Total</th></tr></thead>
            <tbody>
              {j.methods.map((m) => {
                const sel = (chosen || j.recommended) === m.id;
                return (
                  <tr key={m.id} className={`border-t cursor-pointer ${sel ? "bg-emerald-50" : "hover:bg-slate-50"}`} onClick={() => onChoose(jk, m.id)} data-testid={`inst-method-${jk}-${m.id}`}>
                    <td className="px-2 py-1.5"><div className="font-semibold inline-flex items-center gap-1">{sel && <span className="text-emerald-700">★</span>}{m.label}{m.id === j.recommended && <span className="text-[10px] text-emerald-700 font-normal">(recommandée)</span>}</div><div className="text-[10px] text-slate-500">{m.note}</div></td>
                    {m.schedule.map((s) => <td key={s.due} className="px-2 py-1.5 text-right font-mono">{money(s.amount)}</td>)}
                    <td className="px-2 py-1.5 text-right font-mono font-bold">{money(m.total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Calendar({ res }) {
  if (!res.calendar?.length) return null;
  return (
    <div className="bg-white border-2 border-violet-200 rounded-xl p-3" data-testid="inst-calendar">
      <h4 className="text-sm font-bold text-slate-800">Calendrier de versements {res.calendar_note ? "" : "(méthode retenue ★)"}</h4>
      {res.calendar_note && <p className="text-[11px] text-slate-500 mt-0.5">{res.calendar_note}</p>}
      <table className="w-full text-xs mt-2">
        <thead className="text-slate-500"><tr><th className="text-left px-2 py-1">Date</th><th className="text-right px-2 py-1">Fédéral</th><th className="text-right px-2 py-1">Québec</th><th className="text-right px-2 py-1">Total</th></tr></thead>
        <tbody>
          {res.calendar.map((c) => <tr key={c.due} className="border-t"><td className="px-2 py-1 font-mono">{c.due}</td><td className="px-2 py-1 text-right font-mono">{money(c.fed)}</td><td className="px-2 py-1 text-right font-mono">{money(c.qc)}</td><td className="px-2 py-1 text-right font-mono font-bold">{money(c.total)}</td></tr>)}
          <tr className="border-t bg-violet-50 font-bold"><td className="px-2 py-1.5">TOTAL</td><td></td><td></td><td className="px-2 py-1.5 text-right font-mono" data-testid="inst-calendar-total">{money(res.calendar_total)}</td></tr>
        </tbody>
      </table>
    </div>
  );
}

function SavedPlans({ plans, onDelete }) {
  if (!plans.length) return null;
  return (
    <div className="bg-white border rounded-xl p-3" data-testid="inst-saved-plans">
      <h4 className="text-xs font-bold text-slate-700 mb-2">Plans enregistrés</h4>
      <ul className="divide-y text-xs">
        {plans.map((p) => (
          <li key={p.id} className="flex items-center justify-between gap-2 py-1.5" data-testid={`inst-plan-${p.id}`}>
            <span><span className="font-semibold">{p.label}</span> · {KIND_LABEL[p.kind] || p.kind} · {p.result?.calendar?.length || 0} versement(s) · total <span className="font-mono">{money(p.result?.calendar_total || 0)}</span><span className="text-slate-400"> — enregistré le {String(p.saved_at || "").slice(0, 10)}</span></span>
            <button onClick={() => onDelete(p)} className="text-slate-400 hover:text-rose-600 p-1" title="Supprimer le plan" data-testid={`inst-plan-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </li>
        ))}
      </ul>
    </div>
  );
}

const SE_DEFAULT = { province: "QC", age: "", other_income: "", revenue: "", expenses: "", ei_opt_in: false, pension_optional_contribute: true };

export default function InstalmentsCalculator({ scope, masterId = null, fiscalDossierId = null, defaultName = "", allowCorporation = false, defaultKind = "self_employed", pdfMasterId = null }) {
  const fixedKind = scope === "fiscal" ? "individual" : scope === "cabinet" ? "self_employed" : null;
  const [kind, setKind] = useState(fixedKind || defaultKind);
  const [year, setYear] = useState(String(YEAR));
  const [fyEnd, setFyEnd] = useState(`${YEAR}-12-31`);
  const [freq, setFreq] = useState("monthly");
  const [qcRes, setQcRes] = useState(true);
  const [increase, setIncrease] = useState("5");
  const [client, setClient] = useState(defaultName || "");
  const [se, setSe] = useState(SE_DEFAULT);
  const [showHistory, setShowHistory] = useState(false);
  const [inp, setInp] = useState({ fed: { prior: "", second_prior: "", current: "" }, qc: { prior: "", second_prior: "", current: "" } });
  const [chosen, setChosen] = useState({ fed: null, qc: null });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [plans, setPlans] = useState([]);

  useEffect(() => { if (!fixedKind) setKind(defaultKind); }, [defaultKind, fixedKind]);
  useEffect(() => { if (defaultName) setClient(defaultName); }, [defaultName]);
  const loadPlans = useCallback(() => {
    const params = { scope };
    if (scope === "books") params.master_id = masterId;
    if (scope === "fiscal") params.fiscal_dossier_id = fiscalDossierId;
    api.get("/admin/tools/instalments/plans", { params }).then((r) => setPlans(r.data?.items || [])).catch(() => setPlans([]));
  }, [scope, masterId, fiscalDossierId]);
  useEffect(() => { loadPlans(); }, [loadPlans]);

  const set = (jk, k, v) => setInp((x) => ({ ...x, [jk]: { ...x[jk], [k]: v } }));
  const setSeField = (k, v) => setSe((x) => ({ ...x, [k]: v }));
  const isSE = kind === "self_employed";
  const isCorp = kind === "corporation";
  const quebec = isSE ? se.province === "QC" : qcRes;

  const payload = useMemo(() => {
    const juris = (jk) => (kind === "individual"
      ? { prior: num(inp[jk].prior), second_prior: num(inp[jk].second_prior), current: numOrNull(inp[jk].current) }
      : { prior: num(inp[jk].prior), second_prior: num(inp[jk].second_prior), current: isSE ? 0 : num(inp[jk].current) });
    return {
      scope, master_id: masterId || undefined, fiscal_dossier_id: fiscalDossierId || undefined,
      kind, year: Number(year), fy_end: fyEnd, frequency: freq, quebec_resident: quebec, client_name: client,
      increase_pct: kind === "individual" ? num(increase) : undefined,
      ...(isSE ? { province: se.province, age: se.age === "" ? null : Number(se.age), other_income: num(se.other_income), revenue: num(se.revenue), expenses: num(se.expenses), ei_opt_in: !!se.ei_opt_in, pension_optional_contribute: !!se.pension_optional_contribute } : {}),
      fed: juris("fed"), qc: juris("qc"), method: chosen,
    };
  }, [scope, masterId, fiscalDossierId, kind, year, fyEnd, freq, quebec, client, increase, se, inp, chosen, isSE]);

  const compute = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/tools/instalments/compute", payload); setRes(r.data); }
    catch (e) { toast.error(errMsg(e, "Calcul impossible")); } finally { setBusy(false); }
  };
  const choose = async (jk, id) => { const c = { ...chosen, [jk]: id }; setChosen(c); try { const r = await api.post("/admin/tools/instalments/compute", { ...payload, method: c }); setRes(r.data); } catch { /* garde l'affichage */ } };
  const pdf = () => downloadPostPdf("/admin/tools/instalments/compute.pdf", { ...payload, master_id: pdfMasterId || masterId || undefined }, `Acomptes_provisionnels_${isCorp ? fyEnd : year}.pdf`, setBusy);
  const savePlan = async () => {
    setBusy(true);
    try {
      await api.post("/admin/tools/instalments/plans", payload);
      toast.success(scope === "cabinet" ? "Plan enregistré (mes acomptes)" : "Plan enregistré au dossier — les dates alimentent le calendrier de conformité");
      loadPlans();
    } catch (e) { toast.error(errMsg(e, "Enregistrement impossible")); } finally { setBusy(false); }
  };
  const deletePlan = async (p) => {
    if (!window.confirm(`Supprimer le plan ${p.label} ?`)) return;
    try { await api.delete(`/admin/tools/instalments/plans/${p.id}`); toast.success("Plan supprimé"); loadPlans(); }
    catch (e) { toast.error(errMsg(e, "Suppression impossible")); }
  };

  const fiscalLabels = [["prior", `Impôt net payé en ${Number(year) - 1}`], ["second_prior", `Impôt net payé en ${Number(year) - 2} (facultatif)`], ["current", `Estimation ${year} (facultatif — sinon ${Number(year) - 1} × augmentation)`]];
  const histLabels = [["prior", "Impôt net année précédente (avis de cotisation)"], ["second_prior", "Impôt net avant-dernière année"]];
  const corpLabels = [["prior", "Impôt exercice précédent"], ["second_prior", "Impôt avant-dernier exercice"], ["current", "Estimation exercice courant"]];

  return (
    <div className="space-y-3" data-testid={`inst-calculator-${scope}`}>
      <div className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        {!fixedKind && (
          <Field label="Type de contribuable">
            <select className={inputCls} value={kind} onChange={(e) => { setKind(e.target.value); setRes(null); }} data-testid="inst-kind"><option value="self_employed">Travailleur autonome</option><option value="corporation">Société</option></select>
          </Field>
        )}
        {isCorp ? <>
          <Field label="Fin d'exercice"><input type="date" className={inputCls} value={fyEnd} onChange={(e) => setFyEnd(e.target.value)} data-testid="inst-fy-end" /></Field>
          <Field label="Fréquence" hint="Trimestriel : SPCC admissible (revenu imposable ≤ 500 000 $, conformité parfaite)"><select className={inputCls} value={freq} onChange={(e) => setFreq(e.target.value)} data-testid="inst-frequency"><option value="monthly">Mensuelle (12)</option><option value="quarterly">Trimestrielle (4)</option></select></Field>
        </> : <>
          <Field label="Année d'imposition"><input type="number" className={inputCls} value={year} onChange={(e) => setYear(e.target.value)} data-testid="inst-year" /></Field>
          {kind === "individual" && <Field label="Résident du Québec" hint="Seuil 1 800 $ (3 000 $ ailleurs au Canada)"><select className={inputCls} value={qcRes ? "1" : "0"} onChange={(e) => setQcRes(e.target.value === "1")} data-testid="inst-qc-resident"><option value="1">Oui</option><option value="0">Non</option></select></Field>}
          {kind === "individual" && <Field label="% d'augmentation prévu" hint="Appliqué à l'impôt payé l'an dernier"><input type="text" inputMode="decimal" className={`${inputCls} font-mono text-right`} value={increase} onChange={(e) => setIncrease(e.target.value)} data-testid="inst-increase" /></Field>}
        </>}
        <Field label="Nom affiché sur le PDF" className={isSE && fixedKind ? "md:col-span-3" : ""}><input className={inputCls} value={client} onChange={(e) => setClient(e.target.value)} placeholder={scope === "cabinet" ? "Mon nom" : "Nom du client"} data-testid="inst-client-name" /></Field>
      </div>

      {isSE && <SelfEmployedForm v={se} set={setSeField} />}

      {kind === "individual" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <PriorTaxInputs jk="fed" v={inp.fed} set={set} labels={fiscalLabels} hint="Impôt net à payer de l'avis de cotisation (après retenues à la source)." />
          {qcRes && <PriorTaxInputs jk="qc" v={inp.qc} set={set} labels={fiscalLabels} />}
        </div>
      )}
      {isCorp && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <PriorTaxInputs jk="fed" v={inp.fed} set={set} labels={corpLabels} />
          <PriorTaxInputs jk="qc" v={inp.qc} set={set} labels={corpLabels} />
        </div>
      )}
      {isSE && (
        <div className="bg-white border rounded-xl p-3" data-testid="inst-se-history">
          <button type="button" onClick={() => setShowHistory((s) => !s)} className="text-xs font-semibold text-violet-700 hover:underline" data-testid="inst-se-history-toggle">{showHistory ? "▾" : "▸"} Impôt des années précédentes (facultatif — pour les méthodes « sans calcul » et « année précédente »)</button>
          {showHistory && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
              <PriorTaxInputs jk="fed" v={inp.fed} set={set} labels={histLabels} />
              {quebec && <PriorTaxInputs jk="qc" v={inp.qc} set={set} labels={histLabels} />}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <Btn busy={busy} onClick={compute} testId="inst-compute">Calculer</Btn>
        {res && <Btn tone="slate" icon={FileDown} busy={busy} onClick={pdf} testId="inst-pdf">{scope === "cabinet" ? "PDF" : "PDF pour le client"}</Btn>}
        {res && <Btn tone="slate" icon={Save} busy={busy} onClick={savePlan} testId="inst-save-plan">{scope === "cabinet" ? "Enregistrer mon plan" : "Enregistrer au dossier"}</Btn>}
      </div>

      {res && (
        <div className="space-y-3" data-testid="inst-results">
          {res.estimate && <SelfEmployedDetail est={res.estimate} />}
          <Methods jk="fed" j={res.fed} chosen={chosen.fed} onChoose={choose} estimatedFromPrior={res.inputs?.fed?.current_estimated_from_prior} />
          <Methods jk="qc" j={res.qc} chosen={chosen.qc} onChoose={choose} estimatedFromPrior={res.inputs?.qc?.current_estimated_from_prior} />
          <Calendar res={res} />
        </div>
      )}
      <SavedPlans plans={plans} onDelete={deletePlan} />
    </div>
  );
}
